import {
  mdiCheck,
  mdiClipboardEditOutline,
  mdiRotateRight,
  mdiSortAlphabeticalAscending,
  mdiSortAlphabeticalDescending,
  mdiSortAscending,
  mdiSortCalendarAscending,
  mdiSortCalendarDescending,
  mdiSortDescending,
  mdiSortNumericAscending,
  mdiSortNumericDescending,
  mdiThumbDownOutline,
  mdiThumbUpOutline,
  mdiViewGridOutline,
  mdiViewListOutline,
} from '@mdi/js';

const OPERATOR = Object.freeze({
  TYPE: {
    AML_OPERATOR: 'AML Operator',
    AMRO_MANAGER: 'AMRO Manager',
    SAR_MANAGER: 'SAR Manager',
  },
});

const POSITIONINGS = Object.freeze({
  GRID: 'grid',
  LIST: 'list',
});

const QUARTERS = Object.freeze({
  FIRST: 1,
  SECOND: 2,
  THIRD: 3,
  FOURTH: 4,
});

const SCENARIO = Object.freeze({
  RISK: {
    HIGH: 'high',
    MEDIUM: 'medium',
    LOW: 'low',
    REALTIME: 'realtime',
  },
});

const SORTINGS = Object.freeze({
  AMOUNT_ASC: 'amount:asc',
  AMOUNT_DESC: 'amount:desc',
  ASC: 'asc',
  DESC: 'desc',
  CREATED_AT_ASC: 'created_at:asc',
  CREATED_AT_DESC: 'created_at:desc',
  COUNTERPART_LASTNAME_ASC: 'counterpart_lastname:asc',
  COUNTERPART_LASTNAME_DESC: 'counterpart_lastname:desc',
  CREATED_ON_ASC: 'created_on:asc',
  CREATED_ON_DESC: 'created_on:desc',
  DATE_ASC: 'date:asc',
  DATE_DESC: 'date:desc',
  EMAIL_ASC: 'email:asc',
  EMAIL_DESC: 'email:desc',
  FROM_LASTNAME_ASC: 'from_lastname:asc',
  FROM_LASTNAME_DESC: 'from_lastname:desc',
  FROM_WALLET_ASC: 'from_wallet:asc',
  FROM_WALLET_DESC: 'from_wallet:desc',
  INDIVIDUAL_LASTNAME_ASC: 'individual_lastname:asc',
  INDIVIDUAL_LASTNAME_DESC: 'individual_lastname:desc',
  ISSUED_AT_ASC: 'issued_at:asc',
  ISSUED_AT_DESC: 'issued_at:desc',
  LAST_FIRST_NAME_ASC: 'lastname:asc,firstname:asc',
  LAST_FIRST_NAME_DESC: 'lastname:desc,firstname:desc',
  NAME_ASC: 'name:asc',
  NAME_DESC: 'name:desc',
  SAR_STATE_KEY_ASC: 'sar_state_key:asc',
  SAR_STATE_KEY_DESC: 'sar_state_key:desc',
  SCORE_ASC: 'score:asc',
  SCORE_DESC: 'score:desc',
  TO_LASTNAME_ASC: 'to_lastname:asc',
  TO_LASTNAME_DESC: 'to_lastname:desc',
  TO_WALLET_ASC: 'to_wallet:asc',
  TO_WALLET_DESC: 'to_wallet:desc',
  TRANSACTION_DATE_ASC: 'transaction_date:asc',
  TRANSACTION_DATE_DESC: 'transaction_date:desc',
  UPDATED_AT_ASC: 'updated_at:asc',
  UPDATED_AT_DESC: 'updated_at:desc',
});

const STATUS = Object.freeze({
  DONE: 'Done',
  PENDING: 'Pending',
});

const USER = Object.freeze({
  LICENSE: {
    ACTIVE: 'active',
    INACTIVE: 'inactive',
  },
  RISK: {
    HIGH: 'high',
    LOW: 'low',
    MODERATED: 'moderated',
    SEVERE: 'severe',
    UNCLASSIFIED: 'unclassified',
  },
  STATUS: {
    NOT_VERIFIED: 'not_verified',
    RESOLVED: 'resolved',
    REJECTED: 'rejected',
  },
  TYPE: {
    CORPORATE: 'corporate',
    INDIVIDUAL: 'individual',
  },
});

const ICONS = Object.freeze({
  [POSITIONINGS.GRID]: mdiViewGridOutline,
  [POSITIONINGS.LIST]: mdiViewListOutline,
  [SORTINGS.AMOUNT_ASC]: mdiSortNumericAscending,
  [SORTINGS.AMOUNT_DESC]: mdiSortNumericDescending,
  [SORTINGS.ASC]: mdiSortAscending,
  [SORTINGS.DESC]: mdiSortDescending,
  [SORTINGS.COUNTERPART_LASTNAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.COUNTERPART_LASTNAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.CREATED_AT_ASC]: mdiSortCalendarAscending,
  [SORTINGS.CREATED_AT_DESC]: mdiSortCalendarDescending,
  [SORTINGS.CREATED_ON_ASC]: mdiSortCalendarAscending,
  [SORTINGS.CREATED_ON_DESC]: mdiSortCalendarDescending,
  [SORTINGS.DATE_ASC]: mdiSortCalendarAscending,
  [SORTINGS.DATE_DESC]: mdiSortCalendarDescending,
  [SORTINGS.EMAIL_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.EMAIL_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.FROM_LASTNAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.FROM_LASTNAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.FROM_WALLET_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.FROM_WALLET_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.INDIVIDUAL_LASTNAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.INDIVIDUAL_LASTNAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.ISSUED_AT_ASC]: mdiSortCalendarAscending,
  [SORTINGS.ISSUED_AT_DESC]: mdiSortCalendarDescending,
  [SORTINGS.LAST_FIRST_NAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.LAST_FIRST_NAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.NAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.NAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.SAR_STATE_KEY_ASC]: mdiSortAscending,
  [SORTINGS.SAR_STATE_KEY_DESC]: mdiSortDescending,
  [SORTINGS.SCORE_ASC]: mdiSortNumericAscending,
  [SORTINGS.SCORE_DESC]: mdiSortNumericDescending,
  [SORTINGS.TO_LASTNAME_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.TO_LASTNAME_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.TO_WALLET_ASC]: mdiSortAlphabeticalAscending,
  [SORTINGS.TO_WALLET_DESC]: mdiSortAlphabeticalDescending,
  [SORTINGS.TRANSACTION_DATE_ASC]: mdiSortCalendarAscending,
  [SORTINGS.TRANSACTION_DATE_DESC]: mdiSortCalendarDescending,
  [SORTINGS.UPDATED_AT_ASC]: mdiSortCalendarAscending,
  [SORTINGS.UPDATED_AT_DESC]: mdiSortCalendarDescending,
  [STATUS.DONE]: mdiCheck,
  [STATUS.PENDING]: mdiRotateRight,
  [USER.STATUS.NOT_VERIFIED]: mdiClipboardEditOutline,
  [USER.STATUS.RESOLVED]: mdiThumbUpOutline,
  [USER.STATUS.REJECTED]: mdiThumbDownOutline,
});

const LABELS = Object.freeze({
  [POSITIONINGS.GRID]: 'Grid',
  [POSITIONINGS.LIST]: 'List',
  [QUARTERS.FIRST]: 'Primo trimestre',
  [QUARTERS.SECOND]: 'Secondo trimestre',
  [QUARTERS.THIRD]: 'Terzo trimestre',
  [QUARTERS.FOURTH]: 'Quarto trimestre',
  [SORTINGS.AMOUNT_ASC]: 'Amount Asc',
  [SORTINGS.AMOUNT_DESC]: 'Amount Desc',
  [SORTINGS.ASC]: 'Ascending',
  [SORTINGS.DESC]: 'Descending',
  [SORTINGS.COUNTERPART_LASTNAME_ASC]: 'Counterpart Asc',
  [SORTINGS.COUNTERPART_LASTNAME_DESC]: 'Counterpart Desc',
  [SORTINGS.CREATED_AT_ASC]: 'Created Asc',
  [SORTINGS.CREATED_AT_DESC]: 'Created Desc',
  [SORTINGS.CREATED_ON_ASC]: 'Date Asc',
  [SORTINGS.CREATED_ON_DESC]: 'Date Desc',
  [SORTINGS.DATE_ASC]: 'Date Asc',
  [SORTINGS.DATE_DESC]: 'Date Desc',
  [SORTINGS.EMAIL_ASC]: 'Email Asc',
  [SORTINGS.EMAIL_DESC]: 'Email Desc',
  [SORTINGS.FROM_LASTNAME_ASC]: 'From Lastname Asc',
  [SORTINGS.FROM_LASTNAME_DESC]: 'From Lastname Desc',
  [SORTINGS.FROM_WALLET_ASC]: 'From Wallet Asc',
  [SORTINGS.FROM_WALLET_DESC]: 'From Wallet Desc',
  [SORTINGS.INDIVIDUAL_LASTNAME_ASC]: 'Individual Asc',
  [SORTINGS.INDIVIDUAL_LASTNAME_DESC]: 'Individual Desc',
  [SORTINGS.ISSUED_AT_ASC]: 'Issued Asc',
  [SORTINGS.ISSUED_AT_DESC]: 'Issued Desc',
  [SORTINGS.LAST_FIRST_NAME_ASC]: 'Lastname Asc',
  [SORTINGS.LAST_FIRST_NAME_DESC]: 'Lastname Desc',
  [SORTINGS.NAME_ASC]: 'Name Asc',
  [SORTINGS.NAME_DESC]: 'Name Desc',
  [SORTINGS.SAR_STATE_KEY_ASC]: 'State Asc',
  [SORTINGS.SAR_STATE_KEY_DESC]: 'State Desc',
  [SORTINGS.SCORE_ASC]: 'Score Asc',
  [SORTINGS.SCORE_DESC]: 'Score Desc',
  [SORTINGS.TO_LASTNAME_ASC]: 'To Lastname Asc',
  [SORTINGS.TO_LASTNAME_DESC]: 'To Lastname Desc',
  [SORTINGS.TO_WALLET_ASC]: 'To Wallet Asc',
  [SORTINGS.TO_WALLET_DESC]: 'To Wallet Desc',
  [SORTINGS.TRANSACTION_DATE_ASC]: 'Transaction Date Asc',
  [SORTINGS.TRANSACTION_DATE_DESC]: 'Transaction Date Desc',
  [SORTINGS.UPDATED_AT_ASC]: 'Updated Asc',
  [SORTINGS.UPDATED_AT_DESC]: 'Updated Desc',
  [STATUS.DONE]: 'Done',
  [STATUS.PENDING]: 'Pending',
  [USER.STATUS.NOT_VERIFIED]: 'Todo',
  [USER.STATUS.RESOLVED]: 'Approved',
  [USER.STATUS.REJECTED]: 'Rejected',
  [USER.RISK.HIGH]: 'High',
  [USER.RISK.LOW]: 'Low',
  [USER.RISK.MODERATED]: 'Moderated',
  [USER.RISK.SEVERE]: 'Severe',
  [USER.RISK.UNCLASSIFIED]: 'Unclassified',
});

export default Object.freeze({
  ICONS,
  LABELS,
  OPERATOR,
  QUARTERS,
  SCENARIO,
  SORTINGS,
  STATUS,
  USER,
});
